import React from "react";
import styled from "styled-components";
import { Container, Section, Eyebrow } from "../components/ui/Container";
import { colors, fonts, media, radius } from "../theme";

// Cost to borrow UGX 1,000,000 over a 60 day cycle. Indicative figures.
const rows = [
  { p: "Informal lenders", c: 200000, w: 100, tone: "danger", note: "20% per cycle and rising" },
  { p: "Mobile money credit", c: 150000, w: 75, tone: "muted", note: "Capped at small amounts" },
  { p: "Everpesa", c: 90000, w: 45, tone: "brand", note: "Open to small businesses", us: true },
  { p: "Microfinance", c: 70000, w: 35, tone: "muted", note: "Strict requirements" },
  { p: "SACCO members", c: 40000, w: 20, tone: "muted", note: "Members only" },
  { p: "Bank", c: 30000, w: 15, tone: "muted", note: "Most small businesses cannot qualify" },
];

export const Comparison = () => (
  <Section style={{ background: colors.surfaceAlt }}>
    <Container>
      <Head>
        <Eyebrow>The cost of credit, compared</Eyebrow>
        <h2>
          A fairer price for the businesses banks <Hl>can't actually serve</Hl>.
        </h2>
        <p>
          Banks and SACCOs are cheaper on paper but most small businesses cannot
          qualify. Informal lenders are everywhere but compound quickly. Everpesa
          sits in the middle: accessible, transparent, and built for working capital.
          Figures below are indicative, for borrowing UGX 1,000,000 over a 60 day cycle.
        </p>
      </Head>

      <Table>
        {rows.map((r) => {
          const compact = r.w < 22;
          return (
            <Row key={r.p} $us={r.us}>
              <Provider>
                {r.p}
                {r.us && <Badge>Us</Badge>}
              </Provider>
              <BarRow>
                <BarTrack>
                  <BarFill style={{ width: `${r.w}%` }} $tone={r.tone}>
                    {!compact && <Cost>UGX {r.c.toLocaleString()}</Cost>}
                  </BarFill>
                </BarTrack>
                {compact && <CostOutside>UGX {r.c.toLocaleString()}</CostOutside>}
              </BarRow>
              <Note>{r.note}</Note>
            </Row>
          );
        })}
      </Table>
    </Container>
  </Section>
);

const Head = styled.div`
  max-width: 760px;
  margin-bottom: 56px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  h2 { font-family: ${fonts.display}; }
  p { font-size: 17px; }
`;

const Hl = styled.span`
  background: linear-gradient(90deg, ${colors.brand}, ${colors.gold});
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const Table = styled.div`
  background: white;
  border: 1px solid ${colors.line};
  border-radius: ${radius.xl};
  overflow: hidden;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr 180px;
  align-items: center;
  gap: 24px;
  padding: 22px 28px;
  border-bottom: 1px solid ${colors.line};
  background: ${(p) => (p.$us ? colors.brandSoft : "transparent")};

  &:last-child { border-bottom: none; }

  ${media.md} {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 18px 20px;
  }
`;

const Provider = styled.div`
  font-weight: 600;
  font-family: ${fonts.display};
  color: ${colors.ink};
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const Badge = styled.span`
  background: ${colors.brand};
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 999px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BarTrack = styled.div`
  flex: 1;
  height: 36px;
  background: ${colors.surfaceAlt};
  border-radius: 999px;
  overflow: hidden;
  position: relative;
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 999px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  background: ${(p) => {
    if (p.$tone === "danger") return colors.danger;
    if (p.$tone === "brand") return `linear-gradient(90deg, ${colors.brand}, ${colors.brandDark})`;
    return "#5B6478";
  }};
  transition: width 800ms cubic-bezier(0.22,1,0.36,1);
`;

const Cost = styled.span`
  color: white;
  font-weight: 700;
  font-size: 13.5px;
  white-space: nowrap;
`;

const CostOutside = styled.span`
  color: ${colors.ink};
  font-weight: 700;
  font-size: 13.5px;
  white-space: nowrap;
`;

const Note = styled.span`
  font-size: 13px;
  color: ${colors.inkSoft};
  ${media.md} { color: ${colors.inkMuted}; }
`;
